/**
 * Printing a batch of selected orders bag by bag - `FRONTEND.md` §9.472, owner report R2.
 *
 * **The owner:** *"they want to one order to print fiskal, sprievodny, second order the same way...
 * etc... now, we are printing fiskal, order 1, order2, then sprievodny"*. `printFiskalForSelected`
 * issued every receipt in one loop and printed every slip in a second, so a single serial register
 * emitted document-major output and a dispatcher had to pair sixteen sheets across eight bags.
 *
 * **This is a pure orchestrator so that the order of operations is testable.** The sequence lived inside
 * a component, between two `await`s against generated hooks, and nothing could observe it - which is the
 * seam `agos-a-test-on-one-side-of-a-seam` names. The effects are injected; the caller keeps the
 * hooks, the toast and the spinner.
 */

/** Whatever the caller's order shape is - this file classifies, it does not read fields. */
export interface BatchPrintEffects<Order, Payload> {
  /** The receipt payload, or `null` when this screen refuses the order - §9.417. */
  payloadFor: (order: Order) => Payload | null
  /** Issue the tax document. Rejecting is a failure unless {@link alreadyPrinted} says otherwise. */
  receipt: (payload: Payload) => Promise<unknown>
  /** Whether a rejection means *the document already exists* - §9.362. */
  alreadyPrinted: (failure: unknown) => boolean
  /** Print the delivery slip. A rejection is deliberately swallowed - see below. */
  note: (order: Order) => Promise<unknown>
  /**
   * What a failed receipt's refusal said - `BACKEND.md` §10A.379. Optional, so every caller and test
   * written before it reads the same.
   */
  reasonOf?: (failure: unknown) => string
}

/** The four lists §9.417 and §9.362 separated, unchanged. */
export interface BatchPrintOutcome<Order> {
  /** Documents this batch issued. */
  issued: Order[]
  /** Documents the register said it already had - §9.362. They still get a slip. */
  alreadyHad: Order[]
  /** The register refused, or could not be reached. */
  failed: Order[]
  /** **This screen** refused, before the register was asked - §9.417. */
  refused: Order[]
  /**
   * What the first receipt that failed was refused with, or `''` - §10A.379.
   *
   * <p>Kept once, as the slip batch keeps its own (§9.417): a batch fails for one reason far more often than
   * for eight. Without it a dispatcher read *zlyhalo: #1, #2* and nothing else - while the register was
   * saying *Pokladňa nie je aktivovaná (Kasa Rozvoz)*, which is the one thing they could act on.
   */
  reason: string
}

/**
 * One order at a time: its receipt, then its own slip, then the next order.
 *
 * <p><b>Sequential on purpose</b>, which is §9.108's reason unchanged: <em>"a register is a single
 * serial device and firing eight requests at it in parallel is how two documents interleave"</em>. The
 * irony is worth keeping - that comment is about avoiding the wrong interleaving, and this is the right
 * one.
 *
 * <p><b>A slip runs only after its own receipt has just succeeded or was found already present.</b> That
 * is a stronger form of the rule the two loops were written for - <em>"a note for a bag whose receipt
 * failed would send a driver out with an undocumented sale"</em> - because it cannot run for any other
 * order.
 *
 * <p><b>A failed slip stops nothing.</b> <em>"The receipt is the document that matters and it is already
 * issued; a missing slip is a reprint, not a lost sale."</em> A failed **receipt** stops nothing either:
 * one unreachable register must not hold up the other seven.
 */
export async function printBagByBag<Order, Payload>(
  orders: readonly Order[],
  effects: BatchPrintEffects<Order, Payload>,
): Promise<BatchPrintOutcome<Order>> {
  const outcome: BatchPrintOutcome<Order> = {
    issued: [],
    alreadyHad: [],
    failed: [],
    refused: [],
    reason: '',
  }

  for (const order of orders) {
    const payload = effects.payloadFor(order)
    if (payload === null) {
      /* A tax document must say how the sale was paid and this one does not know. Guessing would put a
       * wrong payment method on a real document, so the order is named with the reason and the register
       * is never asked - §9.417. No slip either: there is no document for it to accompany. */
      outcome.refused.push(order)
      continue
    }

    let documented = false
    try {
      await effects.receipt(payload)
      outcome.issued.push(order)
      documented = true
    } catch (caught) {
      if (effects.alreadyPrinted(caught)) {
        outcome.alreadyHad.push(order)
        documented = true
      } else {
        outcome.failed.push(order)
        if (outcome.reason === '' && effects.reasonOf) outcome.reason = effects.reasonOf(caught)
      }
    }

    if (!documented) continue
    try {
      await effects.note(order)
    } catch {
      // Silent by design - see the class note.
    }
  }

  return outcome
}
